// Camera QR scanning. Uses the native BarcodeDetector API where it exists
// (Chrome/Android — fast, hardware-accelerated) and falls back to a bundled
// pure-JS decoder (jsQR) everywhere it doesn't. That fallback is what makes
// scanning work on iOS/Safari, which ships getUserMedia but NOT BarcodeDetector,
// so the feature used to be silently unavailable there.
//
// The decoder is bundled (served from 'self'), so it stays within the strict
// script-src 'self' CSP — no third-party origin is contacted.
//
// Scanning is exposed as an *inline* view: the caller provides a container (a
// small window inside a card), we mount a live <video> into it and decode from
// it. There is no full-screen takeover — the scanner reads as part of the card.

// jsQR is loaded lazily (dynamic import) the first time the fallback path runs,
// so the ~50KB decoder never weighs on initial load for the many users who
// never scan or whose browser has the native BarcodeDetector.
type JsqrFn = typeof import('jsqr').default;
let jsqrPromise: Promise<JsqrFn> | null = null;
function loadJsqr(): Promise<JsqrFn> {
  if (!jsqrPromise) jsqrPromise = import('jsqr').then((m) => m.default);
  return jsqrPromise;
}

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
}

function ctor(): BarcodeDetectorCtor | undefined {
  return (globalThis as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
}

/**
 * Scanning is available wherever we can open the camera — the jsQR fallback
 * covers decoding when BarcodeDetector is missing (notably iOS/Safari). Requires
 * a secure context, where `navigator.mediaDevices` is defined.
 */
export function scanQrSupported(): boolean {
  return typeof navigator.mediaDevices?.getUserMedia === 'function';
}

/** Turn a raw getUserMedia rejection into an actionable French message. */
function cameraError(e: unknown): Error {
  const name = (e as { name?: string } | null)?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new Error(
        'Accès à la caméra refusé. Autorisez-le dans les réglages de votre appareil, ou collez l’ID Cercle à la main.',
      );
    case 'NotFoundError':
    case 'OverconstrainedError':
    case 'DevicesNotFoundError':
      return new Error('Aucune caméra sur cet appareil : collez l’ID Cercle à la main.');
    case 'NotReadableError':
    case 'TrackStartError':
      return new Error('La caméra est déjà utilisée par une autre application.');
    default:
      return e instanceof Error && e.message ? e : new Error('Impossible d’accéder à la caméra.');
  }
}

export interface InlineScan {
  /** Stop the camera and tear down. Safe to call more than once. */
  stop: () => void;
  /** Resolves with the first QR payload; rejects on camera error or stop(). */
  result: Promise<string>;
}

/**
 * Start a live scanner inside `mount` (a small preview window the caller styles
 * and places). Mounts a `<video>` filling the container, opens the rear camera,
 * and decodes frames until a QR is found or `stop()` is called. Rejecting with
 * the message "Scan arrêté" specifically means the caller stopped it.
 */
export function scanQrInline(mount: HTMLElement): InlineScan {
  const Detector = ctor();
  const video = document.createElement('video');
  video.setAttribute('playsinline', 'true');
  video.muted = true; // required for autoplay of a live stream on iOS
  video.className = 'scan-inline-video';
  mount.append(video);

  const detector = Detector ? new Detector({ formats: ['qr_code'] }) : null;

  let stream: MediaStream | null = null;
  let raf = 0;
  let done = false;

  // Reused canvas for the jsQR path (only created when there's no native detector).
  let canvas: HTMLCanvasElement | null = null;
  let cctx: CanvasRenderingContext2D | null = null;
  let lastJsqr = 0;
  let jsqr: JsqrFn | null = null;

  let resolveResult!: (v: string) => void;
  let rejectResult!: (e: Error) => void;
  const result = new Promise<string>((res, rej) => {
    resolveResult = res;
    rejectResult = rej;
  });

  const cleanup = (): void => {
    done = true;
    cancelAnimationFrame(raf);
    stream?.getTracks().forEach((t) => t.stop());
    video.srcObject = null;
    video.remove();
  };

  const stop = (): void => {
    if (done) return;
    cleanup();
    rejectResult(new Error('Scan arrêté'));
  };

  // Decode the current frame with jsQR, downscaled for speed and throttled so it
  // doesn't run every animation frame (heavy on the main thread / battery).
  const decodeJsqr = (now: number): string | null => {
    if (!jsqr || now - lastJsqr < 100) return null;
    lastJsqr = now;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;
    const scale = Math.min(1, 640 / Math.max(vw, vh));
    const w = Math.round(vw * scale);
    const h = Math.round(vh * scale);
    if (!canvas) {
      canvas = document.createElement('canvas');
      cctx = canvas.getContext('2d', { willReadFrequently: true });
    }
    if (!cctx) return null;
    canvas.width = w;
    canvas.height = h;
    cctx.drawImage(video, 0, 0, w, h);
    const image = cctx.getImageData(0, 0, w, h);
    const code = jsqr(image.data, w, h, { inversionAttempts: 'dontInvert' });
    return code?.data ?? null;
  };

  const tick = async (): Promise<void> => {
    if (done) return;
    try {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        let value: string | null = null;
        if (detector) {
          const codes = await detector.detect(video);
          value = codes.length > 0 ? codes[0]!.rawValue : null;
        } else {
          value = decodeJsqr(performance.now());
        }
        if (value) {
          cleanup();
          resolveResult(value);
          return;
        }
      }
    } catch {
      // transient detect/decode errors are ignored; keep scanning
    }
    raf = requestAnimationFrame(() => void tick());
  };

  // Load the decoder (fallback path only) in parallel with opening the camera.
  const decoderReady = detector
    ? Promise.resolve()
    : loadJsqr().then((fn) => {
        jsqr = fn;
      });

  if (!scanQrSupported()) {
    cleanup();
    rejectResult(new Error('Caméra non disponible sur cet appareil'));
    return { stop, result };
  }

  navigator.mediaDevices
    .getUserMedia({ video: { facingMode: 'environment' } })
    .then((s) => {
      if (done) {
        s.getTracks().forEach((t) => t.stop());
        throw new Error('Scan arrêté');
      }
      stream = s;
      video.srcObject = s;
      return video.play();
    })
    .then(() => decoderReady)
    .then(() => {
      if (!done) void tick();
    })
    .catch((e: unknown) => {
      if (done) return; // already stopped; its rejection already fired
      cleanup();
      rejectResult(cameraError(e));
    });

  return { stop, result };
}
